import type { CartItem, CartSnapshot } from '../types/cartTypes';
import { getLineRef } from './cart/lineRef';

/**
 * 对账函数: 根据交互状态决定如何合并 serverCart 和 uiCart
 * 
 * @param uiCart - 当前 UI 状态
 * @param serverCart - 服务器权威状态
 * @param isInteracting - 是否处于交互期
 * @returns 对账后的 CartSnapshot
 */
function addServerItem(
    serverByLineRef: Map<string, CartItem>,
    item: CartItem
) {
    const lineRef = getLineRef(item);
    if (serverByLineRef.has(lineRef)) {
        if (import.meta.env.MODE !== 'production') {
            console.warn('[cart][reconcile] Duplicate lineRef from server', lineRef);
        }
        return;
    }
    serverByLineRef.set(lineRef, item);
}

export function reconcileCart(
    uiCart: CartSnapshot,
    serverCart: CartSnapshot,
    isInteracting: boolean
): CartSnapshot {
    // 若无交互: 全量覆盖
    if (!isInteracting) return serverCart;

    // 交互期策略:
    // - items 顺序: 以 uiCart 为准 (防止 remount/光标问题)
    // - quantity: 保留 uiCart (用户正在操作)
    // - key/price/title: 从 serverCart 同步 (防 400/404,接受价格变动)
    // - totals/count: 保留 uiCart (避免折扣/脚本波动打断体验)
    const serverByLineRef = new Map<string, CartItem>();
    serverCart.items.forEach((item) => addServerItem(serverByLineRef, item));

    const uiItems = uiCart.items.map(uiItem => {
        const serverItem = serverByLineRef.get(getLineRef(uiItem));
        if (!serverItem) return uiItem; // server 没有的行保留 UI 态(可能是 pending add)

        return {
            ...uiItem, // 保留 UI 的 quantity
            key: serverItem.key, // 同步最新 key (防止 400/404)
            price: serverItem.price, // 同步价格变动
            original_price: serverItem.original_price,
            product_title: serverItem.product_title,
            variant_title: serverItem.variant_title
        };
    });

    const uiLineRefs = new Set(uiCart.items.map(getLineRef));

    const appendedServerItems = serverCart.items.filter(item => {
        const lineRef = getLineRef(item);
        return !uiLineRefs.has(lineRef);
    });

    return {
        ...uiCart, // 保留 UI 的 count/totals (total_price, original_total_price, total_discount 等)
        items: [...uiItems, ...appendedServerItems]
    };
}
